/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { AlertNotifications } from 'components/AlertNotification';
import { getCheckoutAlerts, getSitewideAlerts } from 'components/AlertNotification/selector';
import { Button } from 'components/Button';
import { getCartItems } from 'components/Cart/selector';
import connect from 'components/Shared/connect';
import FormError, { FormErrorModal } from 'components/Shared/Form/FormError';
import Spinner from 'components/Shared/Loader';
import Modal from 'components/Shared/Modal';
import { AddressInfo } from 'models/AddressInfo';
import { CheckoutFormData } from 'models/CheckoutFormData';
import { useDispatch, useSelector } from 'react-redux';
import { getFormSyncErrors, InjectedFormProps, reduxForm, SubmissionError } from 'redux-form';
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { EventWithDataHandler } from 'redux-form/lib/Field';
import batchAction from 'store/batching';
import { hasElements, rm, withPage } from 'utils';
import { closePurchModal, onBlurCheckout, submitPurchase } from 'views/CheckoutPage/actions';
import CheckoutProducts from 'views/CheckoutPage/components/CheckoutProducts';
import { ContactInfo } from 'views/CheckoutPage/ContactInfo';
import { HelpSection } from 'views/CheckoutPage/Footer';
import { Header } from 'views/CheckoutPage/Header';
import { SectionTitle } from 'views/CheckoutPage/SectionTitle';
import {
  cartContainsProduct,
  checkoutForm,
  getInitialFormValues,
  getPurchaseValid,
  getPurchaseValidationMessage,
  isContactInfoInvalid,
  isRentalInfoInvalid,
  isRotInformationInvalid
} from 'views/CheckoutPage/selector';
import { ShareCart } from 'views/CheckoutPage/ShareCart';
import { DeliveryInformation } from './deliveryInformation';
import { Payment } from './payment';
import { PriceSummary } from './priceSummary';
import styles from './styles.scss';
import { getPage } from 'components/Pagelayout/actions';
import { createHref } from 'components/Shared/server';
import { AdminCheckoutCharges, AdminHeader } from 'authorization';
import { AdminMenu } from 'authorization';
import AdminDataProvider from 'authorization/context';
import {
  getUnfulfilledProducts,
  selectShouldUseNewCart
} from 'views/CheckoutPage/DeliveryInformation/selector';
import { isCheckoutPage } from 'models/CheckoutPageModel';
import { CheckoutDeliveryItem } from 'models/CheckoutDeliveryItem';
import { useDidMount } from 'hooks';
import { getIndex } from 'services/ApiCartService';
import { cartLoaded } from 'components/Cart/actions';
import { initialState } from 'components/Cart/reducer';
import { getChannel } from 'views/ProductPage/selector';
import { PriceSummaryBob } from 'views/CheckoutPage/components/PriceSummaryBob';
import { selectIsValidated } from 'store/appShell/selector';
import { GtmContinueToPayment } from 'store/googleTagManager/actions';
import { getQaDataAttr } from '@bygghemmait/utilities';

function isSubmitButton(target: EventTarget): target is HTMLButtonElement {
  return (target as HTMLButtonElement).type === 'submit';
}

interface MergedProps {
  isContactInfoInvalid: boolean;
  submitPurchase: (request: CheckoutFormData) => void;
  closePurchModal: () => void;
  purchaseValidationMessage: string;
  purchaseValid: boolean;
  cartContainsProducts: boolean;
  isContactInfoActive: boolean;
  deliveries: CheckoutDeliveryItem[];
  isRotDeduction: boolean;
  isRotInformationInvalid: boolean;
}

const validate = (
  values: CheckoutFormData,
  cartContainsProducts: boolean,
  isRentalInvalid: boolean
) => {
  if (!cartContainsProducts) {
    return { _error: rm('checkout_error_emptycart_body') };
  }

  if (values.payment === undefined) {
    return { _error: rm('error_uservvalidation_payment') };
  }

  if (
    values.payment !== undefined &&
    values.payment.method === 'svea_payment_part' &&
    values.payment.partPaymentItem === undefined
  ) {
    return { _error: rm('error_uservvalidation_partpaymentcode_payment') };
  }

  if (isRentalInvalid) {
    return { _error: 'Ej korrekt angivet datum för upphämning och återlämning' };
  }

  return false;
};

const useAutofillCheckoutUpdate = ({
  isContactInfoInvalid
}: Pick<MergedProps, 'isContactInfoInvalid'>) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isContactInfoInvalid) {
      dispatch(onBlurCheckout('address'));
    }
  }, [dispatch, isContactInfoInvalid]);
};

const CheckoutPage = ({
  handleSubmit,
  isContactInfoInvalid,
  error,
  submitPurchase,
  purchaseValidationMessage,
  purchaseValid,
  closePurchModal,
  cartContainsProducts,
  deliveries,
  isRotDeduction,
  isRotInformationInvalid
}: MergedProps & InjectedFormProps) => {
  const checkoutAlerts = useSelector(getCheckoutAlerts);
  const sitewideAlerts = useSelector(getSitewideAlerts);
  const isBobCart = useSelector(selectShouldUseNewCart);
  const hasAlert = hasElements(sitewideAlerts) || hasElements(checkoutAlerts);
  const hasUnfulfilledProducts = useSelector(getUnfulfilledProducts).length > 0;
  const isValidated = useSelector(selectIsValidated);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  useDidMount(async () => {
    setLoading(true);
    await getIndex(false)
      .then(res => {
        if (!res.cart) {
          dispatch(cartLoaded({ cart: initialState }));
        }
      })
      .finally(() => setLoading(false));
  });

  useAutofillCheckoutUpdate({ isContactInfoInvalid });

  useEffect(() => {
    if (isRotDeduction) {
      void dispatch(getPage(createHref(window.location.toString())));
    }
  }, [isRotDeduction, dispatch]);

  const cartItems = useSelector(getCartItems);

  const isRentalInvalid = useMemo(() => {
    return isRentalInfoInvalid(cartItems);
  }, [cartItems]);

  const submit = (values: CheckoutFormData): Promise<void> => {
    return new Promise((resolve, reject) => {
      const errors = validate(values, cartContainsProducts, isRentalInvalid);
      if (errors) {
        reject(new SubmissionError(errors));
      } else {
        resolve();

        const deliveryEntries = Object.entries(values.deliveries || {});

        const deliveryValues = deliveryEntries.map(([key, delivery]) => {
          const deliveryItem = deliveries.find(item => key.includes(item.identifier));
          const deliveryOptionItem = deliveryItem?.deliveryOptions.find(
            item => item.deliveryOptionIdentifier === delivery.selected
          );

          if (deliveryOptionItem) {
            return [key, { ...delivery, selectedServicePointId: deliveryOptionItem.id }];
          }
          return [key, delivery];
        });

        const usedDeliveryIdentifiers = deliveryEntries.map(([key]) => key);

        const unusedDeliveries = deliveries
          .filter(d => !usedDeliveryIdentifiers.some(u => u.includes(d.identifier)))
          .map(d => [`delivery-${d.identifier}`, '']);

        const allDeliveryValues = deliveryValues
          .filter(([key, _]) => deliveries.some(d => key.toString().includes(d.identifier)))
          .concat(unusedDeliveries);

        const formattedDeliveries = isBobCart
          ? Object.fromEntries(allDeliveryValues)
          : Object.fromEntries(deliveryValues);

        const formData = {
          ...values,
          deliveries: formattedDeliveries
        };
        submitPurchase(formData);
        dispatch(GtmContinueToPayment());
      }
    });
  };

  const preventSubmitOnEnter = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && !isSubmitButton(e.target)) {
      e.preventDefault();
    }
  };

  const disablePaymentField =
    isContactInfoInvalid || isRentalInvalid || hasUnfulfilledProducts || isRotInformationInvalid;
  const shouldHidePaymentField = disablePaymentField && !isValidated;

  return (
    <AdminDataProvider>
      <div className={styles.checkout}>
        <Header />
        <AdminMenu />
        {loading ? (
          <Spinner loading className={styles.spinner} />
        ) : (
          <div className={styles.body}>
            <AdminHeader />
            {hasAlert && (
              <div className={styles.alerts}>
                <AlertNotifications alerts={sitewideAlerts} />
                <AlertNotifications alerts={checkoutAlerts} />
              </div>
            )}
            {cartContainsProducts ? (
              <>
                <div className={styles.section}>
                  <div className={styles.productSectionWrapper}>
                    <AdminCheckoutCharges className={styles.products_title} />
                    <SectionTitle
                      title={rm('checkout_cart_title')}
                      className={styles.products_title}
                      modeToggle={<ShareCart />}
                    />
                    <CheckoutProducts />
                    {isBobCart ? <PriceSummaryBob /> : <PriceSummary />}
                  </div>
                </div>
                <form onKeyPress={preventSubmitOnEnter}>
                  <div className={styles.section}>
                    <ContactInfo />
                  </div>

                  <div>
                    <div className={styles.section}>
                      <DeliveryInformation
                        shouldHideDeliveries={
                          isContactInfoInvalid ||
                          isRentalInvalid ||
                          hasUnfulfilledProducts ||
                          isRotInformationInvalid
                        }
                        disabled={
                          isContactInfoInvalid || isRentalInvalid || isRotInformationInvalid
                        }
                        rentalProductDatesInvalid={isRentalInvalid}
                      />
                    </div>
                    <div className={styles.section}>
                      <Payment
                        disabled={shouldHidePaymentField}
                        handleSubmit={handleSubmit(submit)}
                      />
                    </div>
                  </div>
                  {purchaseValidationMessage && (
                    <PurchaseValidationModal
                      purchaseValid={purchaseValid}
                      purchaseValidationMessage={purchaseValidationMessage}
                      closePurchModal={closePurchModal}
                    />
                  )}
                </form>
              </>
            ) : (
              <EmptyCart />
            )}
          </div>
        )}
        <HelpSection />
      </div>

      <FormErrorModal />
      {error && <FormError error={error} />}
    </AdminDataProvider>
  );
};

const EmptyCart = () => (
  <div className={styles.emptyCart} {...getQaDataAttr('empty-cart')}>
    <p>{rm('checkout_empty')}</p>
    <a href="/">{rm('checkout_go_to_start_page')}</a>
  </div>
);

type PurchaseValidationModalProps = Pick<
  MergedProps,
  'purchaseValid' | 'purchaseValidationMessage' | 'closePurchModal'
>;
const PurchaseValidationModal = ({
  purchaseValid,
  purchaseValidationMessage,
  closePurchModal
}: PurchaseValidationModalProps) => {
  const channel = useSelector(getChannel);
  return (
    <Modal show>
      {purchaseValid && <Spinner loading className={styles.spinner} />}
      <span className={styles.modalMessage}>{purchaseValidationMessage}</span>
      {!purchaseValid && (
        <Button
          type="button"
          onClick={closePurchModal}
          appearance={channel === 'PP' ? 'primary' : 'secondary'}
        >
          {rm('shared_button_close')}
        </Button>
      )}
    </Modal>
  );
};

interface StateProps {
  address: AddressInfo;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: CheckoutFormData | any;
}

interface FormProps {
  channel: string;
  initialValues: CheckoutFormData | Partial<CheckoutFormData>;
  onBlur: EventWithDataHandler<ChangeEvent>;
}
const getDeliveries = withPage(isCheckoutPage, checkoutPage => checkoutPage.deliveries);
export default connect<MergedProps & FormProps, unknown, StateProps>(
  state => ({
    channel: state.appShell.channel,
    initialValues: getInitialFormValues(state.currentPage),
    address: checkoutForm(state, 'address') as AddressInfo,
    errors: getFormSyncErrors('checkout')(state) as CheckoutFormData,
    purchaseValidationMessage: getPurchaseValidationMessage(state.currentPage),
    purchaseValid: getPurchaseValid(state.currentPage),
    cartContainsProducts: cartContainsProduct(state),
    needsCertificateId: state.cart.needsCertificateId,
    deliveries: getDeliveries(state) || [],
    isRotDeduction: state.cart.hasRotDeduction
  }),
  dispatch => ({
    submitPurchase: (request: CheckoutFormData) => dispatch(batchAction(submitPurchase(request))),
    closePurchModal: () => dispatch(closePurchModal())
  }),
  (
    { address, errors, needsCertificateId, cartItems, ...state },
    dispatch,
    props: Record<string, unknown>
  ) => ({
    ...state,
    ...dispatch,
    ...props,
    isContactInfoInvalid: isContactInfoInvalid(address, errors, needsCertificateId),
    isRotInformationInvalid: isRotInformationInvalid(address, state.isRotDeduction, errors)
  })
)(
  reduxForm({
    form: 'checkout',
    destroyOnUnmount: true,
    touchOnBlur: false,
    touchOnChange: true
  })(CheckoutPage)
);
